import { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import manifest from '@curriculum/curriculum_manifest.json';

/**
 * PathNavigator — the primary action on the ACES landing page.
 * Step 1: pick board (CBSE / Kerala)
 * Step 2: pick grade (Plus One / Plus Two)
 * Step 3: pick subject — this card becomes a link to /:board/:grade/:subject
 *
 * Motion: subject (and board/grade) cards get a subtle 3D tilt toward the
 * cursor on hover. The tilt is driven by direct DOM style writes in pointer
 * event handlers — no React state churn on every frame.
 *
 * Accessibility / a11y: reduced-motion check disables tilt entirely.
 * Touch: coarse / no-hover guards ensure nothing happens on mobile.
 */

// ── motion helpers ────────────────────────────────────────────────────────────

/** Returns true when tilt effects should be disabled. */
function shouldDisableMotion(): boolean {
  if (typeof window === 'undefined') return true;
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia('(hover: none)').matches ||
    window.matchMedia('(pointer: coarse)').matches
  );
}

const MAX_TILT = 7; // degrees
const PERSPECTIVE = 800; // px

/**
 * Apply 3D tilt toward cursor position within the card.
 */
function applyTilt(el: HTMLElement, e: React.PointerEvent<HTMLElement>) {
  if (shouldDisableMotion()) return;
  const rect = el.getBoundingClientRect();
  // Normalised offset from card centre: -0.5 … +0.5
  const nx = (e.clientX - rect.left) / rect.width  - 0.5;
  const ny = (e.clientY - rect.top)  / rect.height - 0.5;
  // rotateY: positive tilt tilts the right side away → use +nx
  // rotateX: positive tilt tilts the bottom away → use -ny
  const rx = (-ny * MAX_TILT).toFixed(2);
  const ry = ( nx * MAX_TILT).toFixed(2);
  el.style.transform = `perspective(${PERSPECTIVE}px) rotateX(${rx}deg) rotateY(${ry}deg) scale3d(1.025,1.025,1)`;
  el.style.transition = 'transform 60ms linear';
  el.style.willChange = 'transform';
}

function resetTilt(el: HTMLElement) {
  el.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
  el.style.transition = 'transform 400ms cubic-bezier(0.23,1,0.32,1)';
}

// ── TiltCard wrappers ─────────────────────────────────────────────────────────

/**
 * A `<button>` that tilts toward the cursor on hover.
 * Passes all other props through unchanged.
 */
function TiltButton({
  children,
  className,
  onClick,
  'aria-pressed': ariaPressed,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  'aria-pressed'?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (ref.current) applyTilt(ref.current, e);
  }, []);

  const onPointerLeave = useCallback(() => {
    if (ref.current) resetTilt(ref.current);
  }, []);

  return (
    <button
      ref={ref}
      className={className}
      onClick={onClick}
      aria-pressed={ariaPressed}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      {children}
    </button>
  );
}

/**
 * A `<Link>` that tilts toward the cursor on hover.
 */
function TiltLink({
  children,
  to,
  tabIndex,
  className,
}: {
  children: React.ReactNode;
  to: string;
  tabIndex?: number;
  className?: string;
}) {
  const ref = useRef<HTMLAnchorElement>(null);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLAnchorElement>) => {
    if (ref.current) applyTilt(ref.current, e);
  }, []);

  const onPointerLeave = useCallback(() => {
    if (ref.current) resetTilt(ref.current);
  }, []);

  return (
    <Link
      ref={ref}
      to={to}
      tabIndex={tabIndex}
      className={className}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      {children}
    </Link>
  );
}

// ── types ─────────────────────────────────────────────────────────────────────

type Board = (typeof manifest.boards)[number];
type Grade = Board['grades'][number];
type Subject = Grade['subjects'][number];

const SUBJECT_ICONS: Record<string, { icon: JSX.Element; color: string; bg: string; hoverShadow: string }> = {
  accountancy: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-7 h-7">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M7 8h10M7 12h7M7 16h4" strokeLinecap="round" />
        <path d="M17 14l-2 2 1 1.5" strokeLinecap="round" />
      </svg>
    ),
    color: 'text-acct-600',
    bg: 'bg-acct-50 border-acct-500/30',
    hoverShadow: 'hover:shadow-glow-acct',
  },
  'business-studies': {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-7 h-7">
        <path d="M3 20h18M5 20V10m4 10V6m4 14v-8m4 8V4" strokeLinecap="round" />
      </svg>
    ),
    color: 'text-biz-500',
    bg: 'bg-biz-50 border-biz-500/25',
    hoverShadow: 'hover:shadow-glow-biz',
  },
  economics: {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-7 h-7">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 6v2m0 8v2" strokeLinecap="round" />
        <path d="M9 9.5C9 8.12 10.34 7 12 7s3 1.12 3 2.5c0 1.5-1.5 2-3 2.5-1.5.5-3 1-3 2.5C9 16.12 10.34 17 12 17s3-1.12 3-2.5" strokeLinecap="round" />
      </svg>
    ),
    color: 'text-econ-600',
    bg: 'bg-econ-50 border-econ-500/30',
    hoverShadow: 'hover:shadow-glow-econ',
  },
};

const BOARD_LABELS: Record<string, { short: string; full: string; badge: string }> = {
  cbse:   { short: 'CBSE',   full: 'CBSE (NCERT)',         badge: 'National board' },
  kerala: { short: 'Kerala', full: 'Kerala HSE (SCERT)',    badge: 'State board' },
};


function chapterCount(subj: Subject) {
  return subj.units.reduce((n, u) => n + u.chapters.length, 0);
}

export function PathNavigator() {
  const [boardId, setBoardId] = useState<string | null>(null);
  const [gradeId, setGradeId] = useState<string | null>(null);

  // The header's "CBSE 11" / "Kerala +2" links pass the board and class in router state.
  const location = useLocation();
  useEffect(() => {
    const pick = location.state as { board?: string; grade?: string } | null;
    const board = manifest.boards.find((b) => b.id === pick?.board);
    if (!board) return;
    setBoardId(board.id);
    setGradeId(board.grades.some((g) => g.id === pick?.grade) ? pick!.grade! : null);
  }, [location.key, location.state]);

  const selectedBoard = boardId ? manifest.boards.find((b) => b.id === boardId) ?? null : null;
  const selectedGrade = selectedBoard && gradeId
    ? selectedBoard.grades.find((g) => g.id === gradeId) ?? null
    : null;

  function handleBoardClick(id: string) {
    if (boardId === id) return;
    setBoardId(id);
    setGradeId(null);
  }

  function handleGradeClick(id: string) {
    setGradeId(gradeId === id ? null : id);
  }

  return (
    <div className="rounded-card-lg border border-ink-200/80 bg-cream shadow-card-raised overflow-hidden">
      {/* Step indicator bar */}
      <div className="flex border-b border-ink-200/80 bg-gradient-to-b from-ink-50 to-cream text-xs uppercase tracking-wide">
        <Step n={1} label="Choose board" done={!!boardId} active={!boardId} />
        <Step n={2} label="Choose class" done={!!gradeId} active={!!boardId && !gradeId} />
        <Step n={3} label="Choose subject" done={false} active={!!gradeId} />
      </div>

      {/* ── STEP 1: Board ─────────────────────────── */}
      <div className="p-5 border-b border-ink-100">
        <p className="text-[11px] text-ink-500 uppercase tracking-[0.14em] font-semibold mb-3">1 — Your board</p>
        <div className="grid grid-cols-2 gap-3">
          {manifest.boards.map((b) => {
            const lbl = BOARD_LABELS[b.id] ?? { short: b.id, full: b.name, badge: '' };
            const selected = boardId === b.id;
            return (
              <TiltButton
                key={b.id}
                onClick={() => handleBoardClick(b.id)}
                className={`
                  rounded-xl border-2 px-4 py-3.5 text-left transition-[border-color,background-color,box-shadow] duration-150
                  ${selected
                    ? 'border-accent-500 bg-accent-100/50 shadow-[0_0_0_4px_rgba(224,123,26,0.14)]'
                    : 'border-ink-200 bg-white/50 hover:border-accent-500/50 hover:bg-accent-50'
                  }
                `}
                aria-pressed={selected}
              >
                <div className={`font-serif text-xl font-semibold ${selected ? 'text-accent-600' : 'text-ink-800'}`}>
                  {lbl.short}
                </div>
                <div className="text-xs text-ink-500 mt-0.5">{lbl.full}</div>
                <div className="mt-2 text-xs text-ink-500">{lbl.badge}</div>
              </TiltButton>
            );
          })}
        </div>
      </div>

      {/* ── STEP 2: Grade ─────────────────────────── */}
      <div className={`p-5 border-b border-ink-100 transition-opacity duration-200 ${boardId ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        <p className="text-[11px] text-ink-500 uppercase tracking-[0.14em] font-semibold mb-3">2 — Your class</p>
        <div className="grid grid-cols-2 gap-3">
          {(selectedBoard ?? manifest.boards[0]!).grades.map((g) => {
            const selected = gradeId === g.id;
            const chaps = g.subjects.reduce((n, s) => n + chapterCount(s), 0);
            return (
              <TiltButton
                key={g.id}
                onClick={() => boardId && handleGradeClick(g.id)}
                className={`
                  rounded-xl border-2 px-4 py-3.5 text-left transition-[border-color,background-color,box-shadow] duration-150
                  ${selected
                    ? 'border-accent-500 bg-accent-100/50 shadow-[0_0_0_4px_rgba(224,123,26,0.14)]'
                    : 'border-ink-200 bg-white/50 hover:border-accent-500/50 hover:bg-accent-50'
                  }
                `}
                aria-pressed={selected}
              >
                <div className={`font-serif text-xl font-semibold ${selected ? 'text-accent-600' : 'text-ink-800'}`}>
                  {g.id === '11' ? 'Plus One' : 'Plus Two'}
                </div>
                <div className="text-xs text-ink-500 mt-0.5">Class {g.label}</div>
                <div className="text-xs text-ink-500 mt-2">{chaps} chapters</div>
              </TiltButton>
            );
          })}
        </div>
      </div>

      {/* ── STEP 3: Subject ─────────────────────────── */}
      <div className={`p-5 transition-opacity duration-200 ${gradeId ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        <p className="text-[11px] text-ink-500 uppercase tracking-[0.14em] font-semibold mb-3">3 — Pick a subject</p>
        <div className="grid grid-cols-3 gap-3">
          {(selectedGrade ?? manifest.boards[0]!.grades[0]!).subjects.map((s) => {
            const meta = SUBJECT_ICONS[s.id] ?? { icon: null, color: 'text-ink-600', bg: 'bg-ink-100/60 border-ink-300', hoverShadow: '' };
            const href = `/${boardId ?? 'cbse'}/${gradeId ?? '11'}/${s.id}/`;
            const chaps = chapterCount(s);
            return (
              <TiltLink
                key={s.id}
                to={href}
                tabIndex={gradeId ? 0 : -1}
                className={`
                  group rounded-xl border-2 p-4 flex flex-col items-center text-center gap-2
                  transition-all duration-200
                  ${meta.bg} ${meta.hoverShadow} hover:border-current hover:-translate-y-0.5
                `}
              >
                <span className={`${meta.color} transition-transform duration-200 group-hover:scale-110`}>
                  {meta.icon}
                </span>
                <div>
                  <div className={`font-serif font-semibold text-sm leading-tight ${meta.color}`}>{s.name}</div>
                  <div className="text-xs text-ink-500 mt-0.5">{chaps} chapters</div>
                </div>
                <span className={`text-xs font-medium mt-1 ${meta.color} opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-150`}>
                  Open →
                </span>
              </TiltLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Step({ n, label, done, active }: { n: number; label: string; done: boolean; active: boolean }) {
  return (
    <div
      className={`flex-1 flex items-center gap-2 px-3 py-2.5 border-r last:border-r-0 border-ink-200 transition-colors
        ${active ? 'bg-accent-100/50 text-accent-700' : done ? 'text-ink-500' : 'text-ink-400'}`}
    >
      <span
        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0
          ${active ? 'bg-accent-500 text-ink-900' : done ? 'bg-ink-200 text-ink-600' : 'bg-ink-100 text-ink-300'}`}
      >
        {done ? '✓' : n}
      </span>
      <span className="hidden sm:block text-xs font-medium">{label}</span>
    </div>
  );
}
